<template>
  <v-row align="center" justify="center" class="mx-2">
    <v-col cols="12">
      <h1>Group posts</h1>
      <v-row>
        <v-col cols="12">
          <div v-if="postList.length == 0 && !this.$apollo.loading">
            <h4>No posts have been created by this user</h4>
          </div>
          <v-card v-for="post in postList" :key="post.node.id" class="my-2 elevation-2">
            <v-card-text class="d-flex justify-space-between mb-0 pb-0">
              <div class="d-flex flex-row">
                <user-avatar
                  :profile-picture="post.node.user.userProfile.profilePicture"
                  :user="post.node.user"
                  size="40"
                />
                <div>
                  <span class="subtitle-2 navy--text pl-3 block text-left">
                    {{ post.node.user.firstName }} {{ post.node.user.lastName }}
                  </span>
                  <span class="caption navy--text pl-3 block text-left">
                    {{ $moment(post.node.createdAt).fromNow() }}
                    {{ post.node.group.title }}
                  </span>
                </div>
              </div>
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small icon v-bind="attrs" v-on="on">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    v-if="post.node.user.id != $store.getters.user.id"
                    link
                    @click="reportPost(post.node.id)"
                  >
                    <v-list-item-title>Report</v-list-item-title>
                  </v-list-item>
                  <v-list-item link @click="handleConfirm(post.node.id)">
                    <v-list-item-title class="p-text">Delete Post</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-text>
            <v-card-text>
              <div class="flex-grow-1">
                <div class="pt-2 px-3 d-flex flex-row mb-4">
                  <span class="body-2 text-left" v-html="post.node.content" />
                </div>
              </div>

              <div @click="handleImageClick(post.node.url)" :class="{ pointer: post.node.url }">
                <div v-if="post.node.postattachmentSet.length == 1" class="d-flex child-flex">
                  <v-img
                    v-if="post.node.postattachmentSet[0].type === 'IMAGE'"
                    :src="post.node.postattachmentSet[0].file"
                    max-height="300"
                    contain
                    aspect-ratio="1"
                    class="rounded"
                  />
                  <vue-plyr
                    v-if="post.node.postattachmentSet[0].type === 'VIDEO'"
                    :options="{
                      duration: post.node.postattachmentSet[0].timeInSeconds,
                    }"
                  >
                    <video controls crossorigin playsinline>
                      <source size="720" :src="post.node.postattachmentSet[0].file" type="video/mp4" />
                    </video>
                  </vue-plyr>
                </div>
                <agile
                  v-else-if="post.node.postattachmentSet.length > 1"
                  :perPage="1"
                  pagination-color="grey"
                  pagination-active-color="navy"
                >
                  <div v-for="attachment in post.node.postattachmentSet" :key="attachment.id" class="slide">
                    <div v-if="attachment.type === 'IMAGE'" class="d-flex child-flex">
                      <v-img
                        :src="attachment.file"
                        class="elevation-2 rounded"
                        max-height="300"
                        contain
                        aspect-ratio="1"
                      />
                    </div>
                    <vue-plyr
                      v-if="attachment.type === 'VIDEO'"
                      :options="{
                        duration: attachment.timeInSeconds,
                      }"
                    >
                      <video controls crossorigin playsinline>
                        <source size="720" :src="attachment.file" type="video/mp4" />
                      </video>
                    </vue-plyr>
                  </div>
                </agile>
              </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mx-2">
              <v-spacer />

              <v-btn icon color="navy" @click="handleCommentClick(post.node.id)">
                <v-icon small>mdi-comment-outline</v-icon>
              </v-btn>
              <span class="p-text mr-2 ml-n1">{{ post.node.commentCount }}</span>

              <post-like :userPostLike="post.node.userPostLike" :postId="post.node.id" @like-update="handleLike" />
              <span class="p-text ml-n1">{{ post.node.likeCount }}</span>
            </v-card-actions>
            <v-card-text v-if="showComment.includes(post.node.id)" class="pt-0">
              <post-comment :postId="post.node.id" @comment-update="handleComment" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <scroll-loader :loader-method="loadmore" :loader-disable="!hasNextPage"> </scroll-loader>
    </v-col>
    <v-dialog v-model="deleteDialog" width="500">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          Are you sure you want to delete this post?
        </v-card-title>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="secondary" @click="handleClose()">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn color="background-grey" class="secondary--text pa-2" @click="deletePost()">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { POSTS_QUERY } from "@/graphql/queries/post";
import { CREATE_MODERATION_MUTATION, DELETE_POST_MUTATION } from "@/graphql/mutations/group";
import PostLike from "@/components/social/interactions/PostLike";
import PostComment from "@/components/social/interactions/PostComment";
import UserAvatar from "@/components/shared/user/UserAvatar";
import { decodeId } from "@/lib/string";
import { dedupeNodes } from "@/lib/array";
import { VueAgile } from "vue-agile";

export default {
  name: "UserPosts",
  components: {
    PostLike,
    PostComment,
    agile: VueAgile,
    UserAvatar,
  },
  props: ["userId"],
  data() {
    return {
      after: "",
      endCursor: "",
      hasNextPage: true,
      posts: [],
      postList: [],
      loading: true,
      showComment: [],
      deleteDialog: false,
      selectedPost: null,
    };
  },
  watch: {
    userId: function() {
      this.reloadThread();
    },
  },
  methods: {
    handleImageClick(url) {
      if (!url) {
        return false;
      }
      //this.$router.push(url);
      // safer to just change location. The URL is most likely absolute, which makes router.push freak out?
      window.location.href = url;
    },
    loadmore() {
      if (!this.loading) {
        this.after = this.endCursor;
      }
    },
    getId(idString) {
      return decodeId(idString);
    },
    async reloadThread() {
      this.loading = true;
      // No, we are not resetting. If we do so, the UX is terrible. Page jumps as all posts are destroyed and rebuilt.
      // See below apollo query for how we a) dedupe b) sort by date
      //this.postList = [];
      this.post = [];
      this.postList = [];
      this.endCursor = "";
      this.after = "";
      this.hasNextPage = true;

      await Object.values(this.$apollo.queries).forEach((query) => query.refetch());
    },
    async reportPost(id) {
      await this.$apollo.mutate({
        mutation: CREATE_MODERATION_MUTATION,
        variables: {
          id: this.getId(id),
          type: "POST",
        },
      });
    },
    handleClose() {
      this.deleteDialog = false;
      this.selectedPostId = null;
    },
    handleConfirm(id) {
      this.deleteDialog = true;
      this.selectedPostId = id;
    },
    async deletePost() {
      if (this.deleteDialog) {
        await this.$apollo.mutate({
          mutation: DELETE_POST_MUTATION,
          variables: {
            id: this.getId(this.selectedPostId),
          },
        });
        this.selecteselectedPostIddPost = null;
        this.deleteDialog = false;
        // when we delete, we can and must reset totally and accept page flashing.
        this.postList = [];
        this.reloadThread();
      }
    },
    handleLike(data) {
      const index = this.postList.findIndex((item) => item.node.id == data.postId);
      if (data.id == null) {
        this.postList[index].node.userPostLike = null;
        this.postList[index].node.likeCount -= 1;
      } else {
        this.postList[index].node.userPostLike = { id: data.id };
        this.postList[index].node.likeCount += 1;
      }
    },
    handleComment(data) {
      const index = this.postList.findIndex((item) => item.node.id == data.postId);
      if (data.type == "delete") {
        this.postList[index].node.commentCount -= 1;
      } else {
        this.postList[index].node.commentCount += 1;
      }
    },
    handleCommentClick(id) {
      if (this.showComment.includes(id)) {
        const arr = this.showComment.filter(function(item) {
          return item !== id;
        });
        this.showComment = [...arr];
      } else {
        this.showComment = [...this.showComment, id];
      }
    },
  },
  apollo: {
    posts: {
      query: POSTS_QUERY,
      variables() {
        let input = {
          userId: btoa("UserNode:" + this.userId),
        };
        input["after"] = this.after;
        input["first"] = 10;

        return input;
      },
      fetchPolicy: "network-only",
      result(response) {
        if (response.data && !response.loading) {
          this.loading = false;
          this.endCursor = response.data.posts.pageInfo.endCursor;
          this.hasNextPage = response.data.posts.pageInfo.hasNextPage;
          let posts = [...this.postList, ...response.data.posts.edges];

          // deduplicate, since we are not resettting posts. This stops the horrid page jump when you add a post
          let dedupedPosts = dedupeNodes(posts);

          // now sort by createdAt
          dedupedPosts.sort(function(a, b) {
            return new Date(b.node.createdAt) - new Date(a.node.createdAt);
          });
          this.postList = dedupedPosts;
        }
      },
      update: (data) => data.posts.edges,
    },
  },
};
</script>
